import { useEffect } from 'react';
import { TECHNIQUES, Technique, practiceShare } from '../techniques';
import { RuleId } from '../../shared/rules';
import { ExampleCarousel, dotColor } from './technique-example';

// The Tips & Tricks entry for one technique, in a dialog — opened by clicking a technique name on
// a player profile. It renders the *same* carousel the reference page does, so the pictures here
// are the real engine's deductions rather than a second copy that could drift, and it carries the
// same "practice this" link: a profile that tells you which technique is your slow one is exactly
// where generating a board that drills it should be one tap away.
//
// R8 (forced queen) has no entry on the reference page — it is too obvious to teach — so
// `techniqueFor` returns undefined for it and the profile leaves that row unclickable.

export const techniqueFor = (rule: string): Technique | undefined =>
  TECHNIQUES.find(t => t.rule === rule);

export function TechniqueModal({ technique, onClose, onPractice }: {
  technique: Technique;
  onClose: () => void;
  /** Open the generator with this technique pre-selected. Absent (the tests) leaves the link a
   *  plain href, which still lands on the right page — it just reloads to get there. */
  onPractice?: (rule: RuleId) => void;
}) {
  // R1 is bookkeeping and R8 has no entry here, so in practice this is every technique shown.
  const trainable = practiceShare(technique.rule) !== undefined;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="tech-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="tech-modal"
        role="dialog"
        aria-modal="true"
        aria-label={technique.name}
        onClick={e => e.stopPropagation()}
      >
        <div className="tech-modal-head">
          <h3 className="tech-name">
            <span className="tech-dot" style={{ background: dotColor(technique.rule) }} />
            {technique.name}
          </h3>
          <button className="tech-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <p className="tech-tagline">{technique.tagline}</p>

        <div className="tech-body">
          <ExampleCarousel technique={technique} />
          <div className="tech-prose">
            {technique.body.map((p, i) => <p key={i}>{p}</p>)}
            <div className="tech-modal-actions">
              {trainable && (
                <a
                  className="btn-secondary tech-practice"
                  href={`/editor?practice=${technique.rule}`}
                  onClick={e => {
                    if (!onPractice || e.metaKey || e.ctrlKey || e.shiftKey) return;
                    e.preventDefault();
                    onPractice(technique.rule as RuleId);
                  }}
                >
                  🎲 Practice this
                </a>
              )}
              {/* A plain link either way — leaving the profile for the full reference page is a
                  navigation the router has nothing to add to. */}
              <a className="tech-modal-more" href={`/tips-and-tricks#${technique.rule}`}>
                Read this on Tips &amp; Tricks →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
